import { get } from 'lodash'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Event } from '@wellesley/openapi'
import { rootReducer } from '@wellesley/store'
import {
  EventsSliceState,
  getEventIndex,
  handleEventAdd,
  handleEventUpdate,
} from './eventsSlice.utils'
import {
  EventConfigs,
  EventScope,
  MyEventsFilters,
  MyEventsFiltersKeys,
} from '../helpers/Event.types'

const initialEventsState: EventsSliceState = {
  configs: {
    scope: null,
    scopeProps: null,
    queryArgs: null,
  },
  items: [],
  filters: null,
  enableNextPageFetch: false,
}

export const EVENTS_PER_PAGE = 25

export const eventsSlice = createSlice({
  name: 'events',
  initialState: initialEventsState,
  reducers: {
    setConfigs: (
      state: EventsSliceState,
      action: PayloadAction<EventConfigs>,
    ) => {
      state.configs = action.payload
      // Reset
      state.filters = null
      state.items = []
      state.enableNextPageFetch = false
    },
    resetConfigs: (state: EventsSliceState) => {
      state.configs = {
        scope: null,
        scopeProps: null,
        queryArgs: null,
      }
      state.filters = null
      state.items = []
      state.enableNextPageFetch = false
    },
    setFilters: (
      state: EventsSliceState,
      action: PayloadAction<MyEventsFilters | null>,
    ) => {
      state.filters = action.payload
    },
    toggleFilters: (
      state: EventsSliceState,
      action: PayloadAction<{
        filterKey: MyEventsFiltersKeys
      }>,
    ) => {
      const { filterKey } = action.payload
      state.filters = {
        ...state.filters,
        [filterKey]: !get(state.filters, filterKey),
      }
    },
    setEnableNextPageFetch: (
      state: EventsSliceState,
      action: PayloadAction<boolean>,
    ) => {
      state.enableNextPageFetch = action.payload
    },
    addEvents: (
      state: EventsSliceState,
      action: PayloadAction<{
        scope: EventScope
        events: Event[]
        isNewList?: boolean
      }>,
    ) => {
      const { scope, events, isNewList } = action.payload

      if (state.configs.scope !== scope) {
        return
      }

      if (isNewList) {
        state.items = events
      } else {
        state.items.push(...events)
      }
    },
    addEvent: (
      state: EventsSliceState,
      action: PayloadAction<{
        event: Event
      }>,
    ) => {
      const { event } = action.payload
      handleEventAdd(state, event, EVENTS_PER_PAGE)
    },
    updateEvent: (
      state: EventsSliceState,
      action: PayloadAction<{
        id: string
        patch: Event | Partial<Event>
        override?: boolean
      }>,
    ) => {
      handleEventUpdate(state, action.payload, EVENTS_PER_PAGE)
    },
    // Used by sockets to add or update event in the list
    syncEvent: (
      state: EventsSliceState,
      action: PayloadAction<{
        event: Event
      }>,
    ) => {
      const { event } = action.payload

      const eventIdx = getEventIndex(state.items, event.id)
      if (eventIdx === -1) {
        handleEventAdd(state, event, EVENTS_PER_PAGE)
      } else {
        handleEventUpdate(
          state,
          {
            id: event.id,
            patch: event,
            override: true,
          },
          EVENTS_PER_PAGE,
        )
      }
    },
    deleteEvent: (
      state: EventsSliceState,
      action: PayloadAction<{
        eventId: string
      }>,
    ) => {
      const { eventId } = action.payload
      const eventIdx = getEventIndex(state.items, eventId)

      if (eventIdx !== -1) {
        state.items.splice(eventIdx, 1)
      }
    },
  },
  selectors: {
    selectConfigs: (state) => state.configs,
    selectScope: (state) => state.configs.scope,
    selectItems: (state) => state.items,
    selectFilters: (state) => state.filters,
    selectEnableNextPageFetch: (state) => state.enableNextPageFetch,
  },
}).injectInto(rootReducer)
